/**
 * Thrivia Embed SDK: the creator list surface.
 *
 * Purpose: render one creator's upcoming events as a responsive grid of the SAME card component the
 * single-event surface uses. There is no second card design and there must never be one: a list item
 * that drifts from the card is two states, two skeletons and two sets of branding to keep honest.
 *
 * What is different from the card, and why:
 *
 * - **One `ItemList`, no per-item `Event` entities.** Ten cards each emitting their own Event
 *   entity is ten duplicate entities for pages a crawler can already reach through their canonical
 *   URLs, which is duplicate-entity spam and earns a structured-data penalty rather than a rich
 *   result. The `ItemList` says what this widget actually is: an ordered list of links to those
 *   pages. Each entry carries `url` (the API's canonical string, verbatim) and `name` only.
 * - **The skeleton is best effort.** A card mount knows it is one card, so its reserved box is the
 *   final box exactly. A list mount does not know how many events the creator has until the API
 *   answers, so it reserves up to three card boxes: enough that the common case does not shift,
 *   honest that a creator with eight events will still push the page down once.
 * - **`items` is already truncated** to the mount's `data-limit` by `index.ts` (default 10, capped
 *   at the backend's 500). Truncating again here would silently disagree with the request.
 *
 * Everything else, including the four states, the XSS posture and the `card.url`-verbatim rule,
 * belongs to `render/card.ts` and is inherited by construction.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

import type { MountRecord, MountRenderer } from "../scan";
import { injectStyles } from "../styles";
import { OWN, SCHEMA, buildCard, buildSkeleton, e, ldScript, swap } from "./card";

/** How many card boxes an empty list reserves. Beyond three the guess costs more than it saves. */
const SKELETONS = 3;

/** The grid. `auto-fill` with a 260px minimum is the whole responsive story; no media query needed. */
function grid(): HTMLElement {
  return e("div", OWN + " thrivia-l", null);
}

/** One frame of a list mount. See `render/card.ts` for the state contract; it is the same one. */
function paint(record: MountRecord): void {
  const state = record.state;
  const items = record.items;
  let node: HTMLElement | null = null;

  if (state === "ready" && items && items.length > 0) {
    injectStyles();
    node = grid();
    const entries: unknown[] = [];
    // One context for every item: they all arrived in the same response, so they share its sid, and
    // a creator's sold out event deserves the notify-me form in a list exactly as it does alone.
    // `surface` is this mount's own mode, so a capture submitted from a list card reaches the CRM
    // as a list rather than as whichever surface the page's batched sid happened to be minted with
    // (CO-6). This mount never joins that batch anyway: the list surface is creator-scoped and makes
    // its own request, so its sid was always right and only the LABEL was at risk.
    const cap = { sid: record.sid, surface: record.mode, preview: record.preview };
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      node.appendChild(buildCard(item, cap, record.theme));
      // `position` is 1 based per schema.org. `url` is the API's canonical string, never rebuilt.
      entries.push({ "@type": "ListItem", position: i + 1, url: item.url, name: item.title });
    }
    node.appendChild(
      ldScript({ "@context": SCHEMA, "@type": "ItemList", itemListElement: entries }),
    );
  } else if (state === "pending" || state === "loading") {
    injectStyles();
    node = grid();
    const limit = record.limit || SKELETONS;
    for (let i = 0; i < (limit < SKELETONS ? limit : SKELETONS); i++) {
      node.appendChild(buildSkeleton());
    }
  }

  swap(record.el, node);
}

/**
 * The list renderer, registered for mode `list` in `index.ts`.
 *
 * No `teardown`, for the same reason as the card: nothing is attached outside `record.el`.
 */
export const listRenderer: MountRenderer = { mount: paint, update: paint };
